import { Loader2 } from "lucide-react";

/**
 * iter56 — Editorial luxury metric tile.
 *
 * Spacious, warm-bordered summary card used on Dashboard + top of Bookings /
 * Team / Post-Prod pages. Composition:
 *
 *   [eyebrow (small caps)]                          [icon in tinted well]
 *   ₹4,04,400   ← big serif value
 *   Client payments received                          ← muted subtitle
 *   ─── optional footer meta row ──────────────────────────
 *
 * `tone` shifts the icon well + optional footer accent color. Defaults to
 * neutral cream/stone. `loading` shows a spinner in the value slot.
 * `to` (optional) wraps the card in a link for one-click drill-through.
 */
const TONES = {
  neutral: { well: "bg-stone-100 text-stone-600", accent: "text-stone-500" },
  wine:    { well: "bg-rose-50 text-rose-800",   accent: "text-rose-700" },
  emerald: { well: "bg-emerald-50 text-emerald-800", accent: "text-emerald-700" },
  amber:   { well: "bg-amber-50 text-amber-800", accent: "text-amber-800" },
  red:     { well: "bg-red-50 text-red-800",     accent: "text-red-700" },
};

export default function MetricCard({
  eyebrow,
  value,
  subtitle,
  icon: Icon,
  tone = "neutral",
  meta,
  loading = false,
  onClick,
  testid,
  serif = true,
}) {
  const t = TONES[tone] || TONES.neutral;
  const clickable = !!onClick;
  const Wrap = clickable ? "button" : "div";
  const wrapProps = clickable
    ? { type: "button", onClick, className: "card-lux text-left w-full p-5 md:p-6 transition hover:shadow-sm hover:border-stone-300 focus:outline-none focus:ring-2 focus:ring-rose-700" }
    : { className: "card-lux p-5 md:p-6" };

  return (
    <Wrap {...wrapProps} data-testid={testid}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          {eyebrow && <div className="editorial-eyebrow mb-3">{eyebrow}</div>}
          <div className={`${serif ? "font-heading" : "font-sans"} text-3xl md:text-[2.25rem] font-semibold text-stone-900 tabular-nums leading-none`}>
            {loading ? <Loader2 className="w-7 h-7 animate-spin text-stone-300" /> : value}
          </div>
          {subtitle && (
            <div className="text-sm text-stone-500 mt-2 leading-snug">{subtitle}</div>
          )}
        </div>
        {Icon && (
          <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${t.well}`}>
            <Icon className="w-5 h-5" strokeWidth={1.8} />
          </div>
        )}
      </div>
      {meta && (
        <div className={`text-[11px] uppercase tracking-widest font-semibold mt-4 pt-3 border-t border-stone-100 ${t.accent}`}>
          {meta}
        </div>
      )}
    </Wrap>
  );
}
